import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const QUIZ_LEAD_FORM_URL = 'https://readdy.ai/api/form/d6rj48ma739gopmlhmmg';

export default function QuizPage() {
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [recommendation, setRecommendation] = useState<{
    policy: string;
    reasoning: string[];
    estimatedCost: string;
    nextSteps: { text: string; link: string }[];
    matchScore: number;
    blogLink: string;
    alternativePolicy: string;
    alternativeReason: string;
    costOfWaiting: string;
  } | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Lead capture state
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '', smsConsent: false });
  const [leadStatus, setLeadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [leadSkipped, setLeadSkipped] = useState(false);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${SITE_URL}/`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Life Insurance Quiz",
        "item": `${SITE_URL}/quiz`
      }
    ]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Life Insurance Quiz - Find Your Perfect Policy",
    "description": "Take our free 2-minute life insurance quiz to discover which policy type is right for you. Get personalized recommendations for term, whole, or universal life insurance in Indiana.",
    "url": `${SITE_URL}/quiz`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${SITE_URL}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Life Insurance Quiz",
          "item": `${SITE_URL}/quiz`
        }
      ]
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Life Insurance Quiz Indiana | Find Your Policy';

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Take our free 2-minute life insurance quiz to find the right policy. Get personalized term, whole & universal life recommendations in Indiana.');
    setMeta('keywords', 'life insurance quiz, policy finder, life insurance recommendations, which life insurance, term vs whole life, life insurance guide Indiana');
    setMeta('og:title', 'Life Insurance Quiz Indiana | Find Your Perfect Policy', true);
    setMeta('og:description', 'Answer 6 quick questions to get personalized life insurance recommendations. Free quiz helps you find the right coverage for your family.', true);
    setMeta('og:url', `${SITE_URL}/quiz`, true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/quiz`);

    let schemaScript = document.getElementById('quiz-breadcrumb-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'quiz-breadcrumb-schema';
      schemaScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify([breadcrumbSchema, webPageSchema]);
  }, [SITE_URL, breadcrumbSchema, webPageSchema]);

  const questions = [
    {
      id: 0,
      question: "What is your primary goal for life insurance?",
      options: [
        { value: "income-replacement", label: "Replace my income for my family", icon: "ri-money-dollar-circle-line" },
        { value: "debt-coverage", label: "Pay off mortgage and debts", icon: "ri-home-4-line" },
        { value: "final-expenses", label: "Cover funeral and final expenses", icon: "ri-hand-heart-line" },
        { value: "wealth-building", label: "Build wealth and leave a legacy", icon: "ri-funds-line" }
      ]
    },
    {
      id: 1,
      question: "How long do you need coverage?",
      options: [
        { value: "temporary", label: "Until my kids are grown (10-30 years)", icon: "ri-time-line" },
        { value: "retirement", label: "Until I retire (30+ years)", icon: "ri-briefcase-line" },
        { value: "lifetime", label: "My entire life", icon: "ri-infinity-line" },
        { value: "unsure", label: "I'm not sure", icon: "ri-question-line" }
      ]
    },
    {
      id: 2,
      question: "What is your current age range?",
      options: [
        { value: "under-30", label: "Under 30", icon: "ri-user-smile-line" },
        { value: "30-45", label: "30-45", icon: "ri-user-line" },
        { value: "46-60", label: "46-60", icon: "ri-user-2-line" },
        { value: "over-60", label: "Over 60", icon: "ri-user-3-line" }
      ]
    },
    {
      id: 3,
      question: "What is your budget for monthly premiums?",
      options: [
        { value: "under-50", label: "Under $50/month", icon: "ri-wallet-line" },
        { value: "50-150", label: "$50-$150/month", icon: "ri-wallet-2-line" },
        { value: "150-300", label: "$150-$300/month", icon: "ri-wallet-3-line" },
        { value: "over-300", label: "Over $300/month", icon: "ri-money-dollar-box-line" }
      ]
    },
    {
      id: 4,
      question: "Do you want to build cash value?",
      options: [
        { value: "yes-priority", label: "Yes, it's a priority", icon: "ri-funds-line" },
        { value: "yes-nice", label: "Yes, but not essential", icon: "ri-line-chart-line" },
        { value: "no", label: "No, just need death benefit", icon: "ri-shield-line" },
        { value: "unsure", label: "I don't know what that means", icon: "ri-question-line" }
      ]
    },
    {
      id: 5,
      question: "How would you describe your health?",
      options: [
        { value: "excellent", label: "Excellent - No health issues", icon: "ri-heart-pulse-line" },
        { value: "good", label: "Good - Minor issues", icon: "ri-heart-line" },
        { value: "average", label: "Average - Some conditions", icon: "ri-heart-2-line" },
        { value: "poor", label: "Fair/Poor - Multiple conditions", icon: "ri-heart-3-line" }
      ]
    }
  ];

  const getAnswerLabel = (questionId: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    const option = question?.options.find(opt => opt.value === value);
    return option?.label || value;
  };

  const handleAnswer = (value: string) => {
    setAnswers({ ...answers, [currentQuestion]: value });
    
    if (currentQuestion < questions.length - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
        setIsTransitioning(false);
      }, 300);
    } else {
      calculateRecommendation({ ...answers, [currentQuestion]: value });
    }
  };

  const calculateRecommendation = (finalAnswers: Record<number, string>) => {
    const goal = finalAnswers[0];
    const duration = finalAnswers[1];
    const age = finalAnswers[2];
    const budget = finalAnswers[3];
    const cashValue = finalAnswers[4];
    const health = finalAnswers[5];

    let recommendedPolicy = '';
    let reasoning = [];
    let estimatedCost = '';
    let nextSteps = [];
    let matchScore = 85;
    let blogLink = '';
    let alternativePolicy = '';
    let alternativeReason = '';
    let costOfWaiting = '';

    // Determine recommendation based on answers
    if (goal === 'final-expenses' || (age === 'over-60' && budget === 'under-50')) {
      recommendedPolicy = 'Final Expense Insurance';
      reasoning = [
        'Designed specifically for funeral and burial costs',
        'Lower coverage amounts keep premiums affordable',
        'Easier approval process for seniors',
        'No medical exam required for most policies'
      ];
      estimatedCost = '$30-$80/month';
      matchScore = 94;
      blogLink = '/blog/final-expense-insurance-indianapolis';
      alternativePolicy = 'Term Life Insurance';
      alternativeReason = 'Offers higher coverage but requires medical underwriting';
      costOfWaiting = 'Waiting just 2 years at age 65 can increase premiums by $15-25/month — that\'s $360-600 more over the life of the policy.';
      nextSteps = [
        { text: 'Learn about Final Expense Insurance', link: '/final-expense' },
        { text: 'Get a personalized quote', link: '/book-appointment' }
      ];
    } else if (duration === 'temporary' && cashValue === 'no' && (budget === 'under-50' || budget === '50-150')) {
      recommendedPolicy = 'Term Life Insurance';
      reasoning = [
        'Most affordable option for temporary coverage',
        'Perfect for covering mortgage and raising children',
        'High coverage amounts at low premiums',
        'Can convert to permanent coverage later'
      ];
      estimatedCost = '$25-$100/month';
      matchScore = 96;
      blogLink = '/blog/term-life-insurance-indianapolis';
      alternativePolicy = 'Whole Life Insurance';
      alternativeReason = 'Builds cash value but costs 5-10x more for the same coverage';
      costOfWaiting = 'A healthy 35-year-old pays ~$30/month. Wait until 40, and that same policy costs ~$45/month — an extra $5,400 over 30 years.';
      nextSteps = [
        { text: 'Explore Term Life options', link: '/term-life' },
        { text: 'Calculate your coverage needs', link: '/instant-quote' }
      ];
    } else if (cashValue === 'yes-priority' && (budget === '150-300' || budget === 'over-300')) {
      recommendedPolicy = 'Whole Life Insurance';
      reasoning = [
        'Guaranteed cash value growth',
        'Fixed premiums that never increase',
        'Lifetime coverage with predictable benefits',
        'Tax-deferred wealth accumulation'
      ];
      estimatedCost = '$200-$600/month';
      matchScore = 93;
      blogLink = '/blog/whole-life-insurance-indianapolis';
      alternativePolicy = 'Universal Life Insurance';
      alternativeReason = 'More flexible but cash value growth is not guaranteed';
      costOfWaiting = 'Starting whole life at 30 vs. 35 saves approximately $2,500-4,000 in total premiums over your lifetime due to lower rates.';
      nextSteps = [
        { text: 'Learn about Whole Life benefits', link: '/whole-life' },
        { text: 'Compare Term vs Whole Life', link: '/term-vs-whole-life' }
      ];
    } else if (goal === 'wealth-building' && duration === 'lifetime') {
      recommendedPolicy = 'Indexed Universal Life Insurance';
      reasoning = [
        'Market-linked growth with downside protection',
        'Flexible premiums and adjustable coverage',
        'Higher growth potential than whole life',
        'Tax-free policy loans for retirement income'
      ];
      estimatedCost = '$300-$800/month';
      matchScore = 91;
      blogLink = '/blog/indexed-universal-life-indianapolis';
      alternativePolicy = 'Whole Life Insurance';
      alternativeReason = 'More predictable but lower growth potential';
      costOfWaiting = 'Every year you delay costs you compound growth. Starting IUL at 35 instead of 30 can mean $50,000-100,000 less cash value at retirement.';
      nextSteps = [
        { text: 'Discover IUL advantages', link: '/indexed-universal-life' },
        { text: 'Schedule a strategy session', link: '/wealth-strategy-call' }
      ];
    } else if (duration === 'lifetime' || duration === 'retirement') {
      recommendedPolicy = 'Universal Life Insurance';
      reasoning = [
        'Flexible permanent coverage',
        'Adjustable premiums and death benefit',
        'Cash value accumulation with interest',
        'More affordable than whole life'
      ];
      estimatedCost = '$150-$400/month';
      matchScore = 89;
      blogLink = '/blog/universal-life-insurance-indianapolis';
      alternativePolicy = 'Term Life Insurance';
      alternativeReason = 'Much cheaper but coverage expires after the term ends';
      costOfWaiting = 'Locking in universal life rates early saves money. Waiting 5 years can increase premiums by 20-30% for the same coverage.';
      nextSteps = [
        { text: 'Learn about Universal Life', link: '/universal-life' },
        { text: 'Get a free consultation', link: '/book-appointment' }
      ];
    } else {
      recommendedPolicy = 'Term Life Insurance';
      reasoning = [
        'Best starting point for most families',
        'Affordable coverage for your needs',
        'Can be converted to permanent later',
        'Flexible term lengths available'
      ];
      estimatedCost = '$30-$120/month';
      matchScore = 88;
      blogLink = '/blog/term-life-insurance-indianapolis';
      alternativePolicy = 'Whole Life Insurance';
      alternativeReason = 'Permanent coverage with cash value but significantly higher premiums';
      costOfWaiting = 'Life insurance rates increase 8-10% per year of age. A 30-year-old pays half what a 40-year-old pays for the same term policy.';
      nextSteps = [
        { text: 'Explore Term Life options', link: '/term-life' },
        { text: 'Get an instant quote', link: '/instant-quote' }
      ];
    }

    setRecommendation({
      policy: recommendedPolicy,
      reasoning,
      estimatedCost,
      nextSteps,
      matchScore,
      blogLink,
      alternativePolicy,
      alternativeReason,
      costOfWaiting
    });
    setShowResults(true);
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestion(currentQuestion - 1);
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handleReset = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setRecommendation(null);
    setLeadData({ name: '', email: '', phone: '', smsConsent: false });
    setLeadStatus('idle');
    setLeadSkipped(false);
    window.scrollTo(0, 0);
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLeadStatus('loading');
    try {
      const body = new URLSearchParams();
      body.append('name', leadData.name);
      body.append('email', leadData.email);
      body.append('phone', leadData.phone);
      body.append('sms_consent', leadData.smsConsent ? 'Yes' : 'No');
      body.append('recommended_policy', recommendation?.policy || '');
      body.append('estimated_cost', recommendation?.estimatedCost || '');
      await fetch(QUIZ_LEAD_FORM_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const nameParts = leadData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const followUpTime = new Date();
      followUpTime.setHours(followUpTime.getHours() + 24);

      supabase.from('leads').insert({
        first_name: firstName,
        last_name: lastName,
        email: leadData.email,
        phone: leadData.phone,
        goals: recommendation?.policy || '',
        notes: `Quiz result: ${recommendation?.policy}. Estimated: ${recommendation?.estimatedCost}.`,
        source: 'Insurance Quiz',
        stage: 'new',
        follow_up_scheduled_at: followUpTime.toISOString(),
        follow_up_sent: false,
      }).then(() => {}).catch(() => {});

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'new_lead',
          lead: {
            name: leadData.name,
            email: leadData.email,
            phone: leadData.phone,
            service: recommendation?.policy || 'Quiz Recommendation',
            message: `Quiz recommendation: ${recommendation?.policy}. Estimated cost: ${recommendation?.estimatedCost}.`,
          },
        }),
      }).catch(() => {});

      setLeadStatus('success');
    } catch {
      setLeadStatus('error');
    }
  };

  const testimonials = [
    {
      name: 'Sarah M.',
      location: 'Fishers, IN',
      text: 'The quiz helped me realize term life was perfect for our young family. We got $500k coverage for just $42/month!',
      policy: 'Term Life'
    },
    {
      name: 'Michael R.',
      location: 'Carmel, IN',
      text: 'I thought life insurance was too expensive. The agent showed me how affordable whole life could be for building wealth.',
      policy: 'Whole Life'
    },
    {
      name: 'Linda K.',
      location: 'Indianapolis, IN',
      text: 'At 68, I thought it was too late. Final expense insurance gave me peace of mind without breaking the bank.',
      policy: 'Final Expense'
    }
  ];

  return (
    <>
      <Header />

      {/* Sticky Progress Indicator - Only show during quiz */}
      {!showResults && (
        <div className="fixed top-20 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md z-40 transition-all duration-300">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-600">
                Question {currentQuestion + 1} of {questions.length}
              </span>
              <span className="text-sm font-semibold text-[#1A3A52]">
                {Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#1A3A52] to-[#D97642] transition-all duration-500 ease-out"
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
            {/* Mini question indicators */}
            <div className="flex items-center justify-center gap-2 mt-3">
              {questions.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-8 h-1 rounded-full transition-all duration-300 ${
                    idx < currentQuestion
                      ? 'bg-[#D97642]'
                      : idx === currentQuestion
                      ? 'bg-[#1A3A52] w-12'
                      : 'bg-gray-300'
                  }`}
                ></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-50 via-white to-amber-50 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-questionnaire-line text-lg"></i>
              Find Your Perfect Policy in 2 Minutes
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Which Life Insurance Is Right for You?
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Answer 6 quick questions and get a <strong>personalized recommendation</strong> for the best life insurance policy based on your goals, budget, and family situation.
            </p>
          </div>
        </div>
      </section>

      {/* Quiz Section */}
      <section className={`py-20 bg-white ${!showResults ? 'pt-32' : ''}`}>
        <div className="max-w-4xl mx-auto px-6">
          {!showResults ? (
            <>
              {/* Question with animated transition */}
              <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-10 shadow-lg">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                    {questions[currentQuestion].question}
                  </h2>

                  <div className="grid md:grid-cols-2 gap-4">
                    {questions[currentQuestion].options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleAnswer(option.value)}
                        className={`p-6 text-left rounded-xl border-2 transition-all hover:border-[#D97642] hover:shadow-lg cursor-pointer ${
                          answers[currentQuestion] === option.value
                            ? 'bg-[#1A3A52] text-white border-[#1A3A52]'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            answers[currentQuestion] === option.value
                              ? 'bg-white/20'
                              : 'bg-[#D97642]/10'
                          }`}>
                            <i className={`${option.icon} text-2xl ${
                              answers[currentQuestion] === option.value ? 'text-white' : 'text-[#D97642]'
                            }`}></i>
                          </div>
                          <span className="font-semibold text-lg">{option.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {currentQuestion > 0 && (
                    <div className="flex justify-start mt-8">
                      <button
                        onClick={handleBack}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-arrow-left-line mr-2"></i>
                        Previous Question
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-8">
              {/* Coverage Profile Summary Card */}
              <div className="bg-gradient-to-br from-[#F9F7F4] to-white rounded-2xl p-8 shadow-lg border-2 border-[#D97642]/20">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center">
                    <i className="ri-file-list-3-line text-2xl text-[#D97642]"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Your Coverage Profile</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {questions.map((question, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {question.question}
                      </div>
                      <div className="flex items-center gap-2">
                        <i className={`${question.options.find(opt => opt.value === answers[idx])?.icon} text-[#D97642] text-lg`}></i>
                        <span className="text-sm font-medium text-gray-900">
                          {getAnswerLabel(idx, answers[idx])}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results Header with Match Score */}
              <div className="bg-gradient-to-br from-[#1A3A52] to-[#0D2235] rounded-2xl p-10 shadow-xl text-white">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <i className="ri-check-line text-lg"></i>
                    Your Personalized Recommendation
                  </div>
                  <h2 className="text-4xl font-bold mb-4">
                    We Recommend: <span className="text-[#D97642]">{recommendation?.policy}</span>
                  </h2>
                  <p className="text-xl opacity-90 mb-8">
                    Based on your answers, this policy type best fits your needs and goals
                  </p>

                  {/* Match Score Bar */}
                  <div className="max-w-md mx-auto">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold">Policy Match Score</span>
                      <span className="text-2xl font-bold text-[#D97642]">{recommendation?.matchScore}%</span>
                    </div>
                    <div className="w-full h-4 bg-white/20 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#D97642] to-amber-400 transition-all duration-1000 rounded-full"
                        style={{ width: `${recommendation?.matchScore}%` }}
                      ></div>
                    </div>
                    <p className="text-sm opacity-75 mt-2">Excellent match for your situation</p>
                  </div>
                </div>

                {/* Direct Deep-Link CTA with Match Score */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                  <Link
                    to={recommendation?.nextSteps[0]?.link || '/term-life'}
                    className="bg-[#D97642] hover:bg-[#C26638] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap text-center flex items-center justify-center gap-2"
                  >
                    <i className="ri-arrow-right-circle-line text-2xl"></i>
                    View {recommendation?.policy} Details
                  </Link>
                  <Link
                    to="/instant-quote"
                    className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all border-2 border-white/30 whitespace-nowrap text-center"
                  >
                    Get Instant Quote
                  </Link>
                </div>
              </div>

              {/* Why This Policy Section */}
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why This Policy Is Right for You</h3>
                <ul className="space-y-4">
                  {recommendation?.reasoning.map((reason, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-2xl text-[#D97642] flex-shrink-0 mt-1"></i>
                      <span className="text-gray-700 text-lg">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* How This Policy Works - Visual Diagram */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">How This Policy Works</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-[#1A3A52] rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-hand-coin-line text-3xl text-white"></i>
                    </div>
                    <div className="w-12 h-1 bg-[#D97642] mx-auto mb-4"></div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">1. Pay Premium</h4>
                    <p className="text-gray-600 text-sm">You make affordable monthly or annual payments to keep your policy active</p>
                  </div>
                  <div className="text-center">
                    <div className="w-20 h-20 bg-[#D97642] rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-shield-check-line text-3xl text-white"></i>
                    </div>
                    <div className="w-12 h-1 bg-[#D97642] mx-auto mb-4"></div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">2. Build Coverage</h4>
                    <p className="text-gray-600 text-sm">Your policy provides guaranteed death benefit protection for your loved ones</p>
                  </div>
                  <div className="text-center">
                    <div className="w-20 h-20 bg-[#1A3A52] rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-heart-3-line text-3xl text-white"></i>
                    </div>
                    <div className="w-12 h-1 bg-[#D97642] mx-auto mb-4"></div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">3. Family Protected</h4>
                    <p className="text-gray-600 text-sm">Your family receives tax-free funds to cover expenses and maintain their lifestyle</p>
                  </div>
                </div>
              </div>

              {/* Cost of Waiting - Urgency Block */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 shadow-lg">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">What Happens If You Don't Act</h3>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {recommendation?.costOfWaiting}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 mt-4">
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <i className="ri-information-line text-[#D97642]"></i>
                    <strong>The best time to get life insurance is now.</strong> Rates increase with age and health changes can make coverage more expensive or unavailable.
                  </p>
                </div>
              </div>

              {/* Comparison Table */}
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Policy Comparison</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-4 px-4 font-bold text-gray-900">Feature</th>
                        <th className="text-center py-4 px-4 font-bold text-[#D97642]">
                          {recommendation?.policy}
                          <div className="text-xs font-normal text-gray-600 mt-1">(Recommended)</div>
                        </th>
                        <th className="text-center py-4 px-4 font-bold text-gray-700">
                          {recommendation?.alternativePolicy}
                          <div className="text-xs font-normal text-gray-600 mt-1">(Alternative)</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-4 px-4 text-gray-700">Monthly Cost</td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-[#D97642] text-white rounded-full">
                            <i className="ri-check-line"></i>
                          </span>
                          <div className="text-sm text-gray-600 mt-1">{recommendation?.estimatedCost}</div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-300 text-gray-600 rounded-full">
                            <i className="ri-subtract-line"></i>
                          </span>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-4 px-4 text-gray-700">Best For Your Goals</td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-[#D97642] text-white rounded-full">
                            <i className="ri-check-line"></i>
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-300 text-gray-600 rounded-full">
                            <i className="ri-close-line"></i>
                          </span>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-4 px-4 text-gray-700">Fits Your Budget</td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-[#D97642] text-white rounded-full">
                            <i className="ri-check-line"></i>
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-300 text-gray-600 rounded-full">
                            <i className="ri-close-line"></i>
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-4 px-4 text-gray-700">Why Consider It?</td>
                        <td className="py-4 px-4 text-center text-sm text-gray-600">Perfect match for your situation</td>
                        <td className="py-4 px-4 text-center text-sm text-gray-600">{recommendation?.alternativeReason}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Social Proof Testimonials */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">What Indiana Families Are Saying</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                        ))}
                      </div>
                      <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-gray-900">{testimonial.name}</p>
                          <p className="text-sm text-gray-600">{testimonial.location}</p>
                        </div>
                        <div className="text-xs bg-[#D97642]/10 text-[#D97642] px-3 py-1 rounded-full font-semibold">
                          {testimonial.policy}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Estimated Cost Banner */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-[#D97642]/30 rounded-xl p-6">
                <div className="flex items-center gap-4 mb-3">
                  <i className="ri-money-dollar-circle-line text-3xl text-[#D97642]"></i>
                  <h4 className="text-xl font-bold text-gray-900">Estimated Cost Range</h4>
                </div>
                <p className="text-2xl font-bold text-[#D97642]">{recommendation?.estimatedCost}</p>
                <p className="text-sm text-gray-600 mt-2">
                  Actual premiums vary based on age, health, coverage amount, and specific policy features
                </p>
              </div>

              {/* Learn More Link */}
              <div className="bg-[#1A3A52] rounded-xl p-6 text-center">
                <p className="text-white mb-4">Want to learn more about {recommendation?.policy}?</p>
                <Link
                  to={recommendation?.blogLink || '/blog'}
                  className="inline-flex items-center gap-2 bg-[#D97642] hover:bg-[#C26638] text-white px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap"
                >
                  <i className="ri-article-line"></i>
                  Read Our Complete Guide
                </Link>
              </div>

              {/* Next Steps */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Your Next Steps</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {recommendation?.nextSteps.map((step, index) => (
                    <Link
                      key={index}
                      to={step.link}
                      className="bg-gradient-to-r from-[#1A3A52] to-[#0D2235] hover:opacity-90 text-white p-6 rounded-xl font-semibold text-lg text-center transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                    >
                      {step.text}
                      <i className="ri-arrow-right-line ml-2"></i>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={handleReset}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Retake Quiz
                </button>
                <a
                  href="tel:3176482983"
                  className="bg-white hover:bg-gray-50 text-gray-900 px-8 py-3 rounded-lg font-semibold border-2 border-gray-300 transition-all whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call (317) 648-2983
                </a>
              </div>

              {/* Lead Capture Card */}
              {!leadSkipped && (
                <div className="bg-white rounded-2xl shadow-lg border-2 border-[#D97642]/30 p-8">
                  {leadStatus === 'success' ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="w-16 h-16 flex items-center justify-center bg-[#D97642]/10 rounded-full mb-4">
                        <i className="ri-check-line text-[32px] text-[#D97642]"></i>
                      </div>
                      <h3 className="text-[22px] font-bold text-gray-900 mb-2">We'll Be in Touch!</h3>
                      <p className="text-gray-600 max-w-sm">
                        A licensed Indiana agent will reach out with a personalized quote for {recommendation?.policy} — usually within a few hours.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-full shrink-0">
                          <i className="ri-mail-send-line text-[20px] text-[#D97642]"></i>
                        </div>
                        <h3 className="text-[20px] font-bold text-gray-900">Get a Free Personalized Quote for {recommendation?.policy}</h3>
                      </div>
                      <p className="text-gray-600 text-sm mb-6 ml-[52px]">
                        A licensed Indiana agent will review your results and send you a real, no-obligation quote tailored to your situation.
                      </p>
                      <form
                        id="quiz-lead-form"
                        data-readdy-form
                        onSubmit={handleLeadSubmit}
                        className="space-y-4"
                      >
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                              Full Name <span className="text-[#D97642]">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              required
                              placeholder="Jane Smith"
                              value={leadData.name}
                              onChange={(e) => setLeadData((p) => ({ ...p, name: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                              Email <span className="text-[#D97642]">*</span>
                            </label>
                            <input
                              type="email"
                              name="email"
                              required
                              placeholder="jane@example.com"
                              value={leadData.email}
                              onChange={(e) => setLeadData((p) => ({ ...p, email: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                              Phone <span className="text-[#D97642]">*</span>
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              required
                              placeholder="(317) 000-0000"
                              value={leadData.phone}
                              onChange={(e) => setLeadData((p) => ({ ...p, phone: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                            />
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            id="sms-consent-quiz"
                            name="sms_consent"
                            required
                            checked={leadData.smsConsent}
                            onChange={(e) => setLeadData((p) => ({ ...p, smsConsent: e.target.checked }))}
                            className="mt-1 w-4 h-4 text-[#D97642] border-gray-300 rounded focus:ring-[#D97642] cursor-pointer"
                          />
                          <label htmlFor="sms-consent-quiz" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                            I agree to receive text messages from Hoosier Life Insurance. Message and data rates may apply. Reply STOP to opt out. <span className="text-[#D97642]">*</span>
                          </label>
                        </div>

                        {leadStatus === 'error' && (
                          <p className="text-sm text-red-500 flex items-center gap-1.5">
                            <i className="ri-error-warning-line"></i>
                            Something went wrong. Please try again.
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="submit"
                            disabled={leadStatus === 'loading'}
                            className="flex-1 min-w-[200px] py-3.5 bg-[#D97642] hover:bg-[#C26638] text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60"
                          >
                            {leadStatus === 'loading' ? (
                              <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                            ) : (
                              <><i className="ri-send-plane-fill"></i> Send Me My Free Quote</>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setLeadSkipped(true)}
                            className="text-sm text-gray-400 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap underline"
                          >
                            No thanks
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-400 leading-relaxed">
                          By submitting, you agree to be contacted by Hoosier Life Insurance. We never sell your information.
                        </p>
                      </form>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}